import { type TeamResource } from '@/features/iam/api/resources/team'
import { Team, type TeamAddress, type TeamProps } from './Team'
import { type IamApi } from '@/features/iam/api/iam-api'
import DataLoader from 'dataloader'
import { collection } from '@/infra/collection'
import { type PrimeTeamMemberFn } from './TeamMemberStore'
import { type CursorPaged } from '@/infra/paging'
import { Id } from '@/infra/id'
import { maybeParseDate } from '@/utils/date-util'
import type { PrimeTeamProps, PrimeWorkspaceFn } from '@/features/iam'

/**
 * Sets a team from a (partial) resource.
 */
export type PrimeFullTeamFn = (resource: Partial<TeamResource>) => Team

export interface ListUserTeamsQuery {
  userPublicId: Id
  searchText?: string
  workspaceIds?: Id[]
  after?: string | null
  limit?: number
}

export interface ListTeamsQuery {
  workspaceId: Id
  after?: string | null
  limit?: number
}

export interface CreateTeamParams {
  name: string
  slug: string
  workspaceId: Id
}

export interface UpdateTeamParams {
  name: string
  teamPurpose: string
  address: TeamAddress
}

export class TeamStore {
  private readonly _teamLoader = new DataLoader(this._fetchMany.bind(this))

  readonly teams = collection({
    getDataId: (data) => data.id,
    getModelId: (model) => model.id,
    create: () => new Team(),
  })

  constructor(
    private readonly _api: IamApi,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
    private readonly _primeTeamMember: PrimeTeamMemberFn,
  ) {}

  /**
   * Sets the team and fetches it in the background.
   */
  primeTeam(props: PrimeTeamProps): Team {
    if (props.id) {
      void this.fetchTeam(props.id)
    }
    return this.teams.set({ id: props.id })
  }

  /**
   * Sets the team from the resource without fetching it.
   */
  primeFullTeam(resource: Partial<TeamResource>): Team {
    return this.teams.set(this._mapTeamResourceToProps(resource))
  }

  /**
   * Fetches a team.
   */
  async fetchTeam(teamId: Id) {
    return this._teamLoader.load(Id.assert(teamId))
  }

  /**
   * Gets the loaded teams of a workspace.
   */
  forWorkspace(workspaceId: Id) {
    const id = Id.assert(workspaceId)
    return this.teams.filter((t) => t.workspace.id === id)
  }

  /**
   * Lists a workspace's teams.
   */
  async listTeams(
    query: ListTeamsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Team>> {
    const response = await this._api.teams.list(
      {
        workspace_id: Id.assert(query.workspaceId),
        after: query.after,
        limit: query.limit,
      },
      signal,
    )
    return {
      data: this.teams.set(response.data.map(this._mapTeamResourceToProps)),
      next: response.cursor,
      total: response.total,
    }
  }

  /**
   * Creates a team.
   */
  async createTeam(params: CreateTeamParams): Promise<Team> {
    return this._api.teams
      .create({
        name: params.name,
        slug: params.slug,
        workspace_id: Id.assert(params.workspaceId),
      })
      .then((data) => this.teams.set(this._mapTeamResourceToProps(data)))
  }

  /**
   * Updates a team.
   */
  async updateTeam(teamId: Id, params: UpdateTeamParams) {
    return this._api.teams
      .update(Id.assert(teamId), {
        name: params.name,
        team_purpose: params.teamPurpose,
        address: {
          street_address: params.address.streetAddress,
          zip_code: params.address.zipCode,
          city: params.address.city,
          state: params.address.state,
          country: params.address.country,
        },
      })
      .then((data) => this.teams.set(this._mapTeamResourceToProps(data)))
  }

  /**
   * Implementation of fetching many teams.
   */
  private async _fetchMany(ids: readonly Id[]) {
    const result = await this._api.teams
      .getMany(ids.map((id) => ({ id: Id.some(id) ? id : '' })))
      .then((data) =>
        this.teams.set(
          data.map((d) => (d ? this._mapTeamResourceToProps(d) : d)),
        ),
      )
    result.forEach((t, i) => {
      if (!t) {
        // Just because a team was not found does not mean it will never exist.
        this._teamLoader.clear(ids[i])
      }
    })

    return result
  }

  /**
   * Uploads a new avatar for the team.
   */
  async changeAvatar(teamId: Id, file: File) {
    const data = await this._api.teams.changeAvatar(Id.assert(teamId), file)
    if (data) {
      this.teams.set(this._mapTeamResourceToProps(data))
    }
  }

  /**
   * Lists the teams of a user.
   */
  async listUserTeams(
    query: ListUserTeamsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Team>> {
    const response = await this._api.users.listTeams(
      {
        userPublicId: Id.assert(query.userPublicId),
        searchText: query.searchText,
        workspaceIds: query.workspaceIds?.map(Id.assert),
        after: query.after,
        limit: query.limit,
      },
      signal,
    )
    return {
      data: this.teams.set(response.data.map(this._mapTeamResourceToProps)),
      next: response.cursor,
      total: response.total,
    }
  }

  private readonly _mapTeamResourceToProps = (
    resource: Partial<TeamResource>,
  ): Partial<TeamProps> => ({
    id: resource.id,
    dateCreated: maybeParseDate(resource.date_created),
    dateModified: maybeParseDate(resource.date_modified),
    name: resource.name,
    slug: resource.slug,
    teamPurpose: resource.team_purpose,
    picture: resource.picture,
    workspace: this._primeWorkspace({ id: resource.workspace_id }),
    address: resource.address && {
      zipCode: resource.address.zip_code,
      city: resource.address.city,
      state: resource.address.state,
      streetAddress: resource.address.street_address,
      country: resource.address.country,
    },
    owner: resource.owner_id
      ? this._primeTeamMember({
          userId: resource.owner_id,
          userPublicId: resource.owner_public_id,
          teamId: resource.id,
          workspaceId: resource.workspace_id,
        })
      : undefined,
  })
}
